import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Ticket, Tag, Calendar, DollarSign, Percent,
  Save, X, Loader, AlertCircle
} from 'lucide-react';
import adminService, {
  CreateCouponRequest,
  Offer
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';

const CreateCoupon: React.FC = () => {
  const navigate = useNavigate();

  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [formData, setFormData] = useState<CreateCouponRequest>({
    coupon_code: '',
    offer_id: null,
    discount_type: 'percentage',
    discount_value: 0,
    min_purchase_amount: undefined,
    max_discount_amount: undefined,
    max_usage: undefined,
    max_usage_per_user: undefined,
    valid_from: '',
    valid_until: '',
    is_active: true,
    description: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const data = await adminService.getAllOffers({ limit: 100 });
      setOffers(data.offers.filter(o => o.is_active === 1));
    } catch (error: any) {
      console.error('Failed to fetch offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCouponCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, coupon_code: code });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.coupon_code.trim()) {
      newErrors.coupon_code = 'Coupon code is required';
    } else if (formData.coupon_code.length < 3) {
      newErrors.coupon_code = 'Coupon code must be at least 3 characters';
    }

    if (formData.discount_value <= 0) {
      newErrors.discount_value = 'Discount value must be greater than 0';
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Valid from date is required';
    }

    if (!formData.valid_until) {
      newErrors.valid_until = 'Valid until date is required';
    }

    if (formData.valid_from && formData.valid_until) {
      const fromDate = new Date(formData.valid_from);
      const untilDate = new Date(formData.valid_until);
      if (untilDate <= fromDate) {
        newErrors.valid_until = 'Valid until must be after valid from date';
      }
    }

    if (formData.min_purchase_amount !== undefined && formData.min_purchase_amount < 0) {
      newErrors.min_purchase_amount = 'Minimum purchase amount cannot be negative';
    }

    if (formData.max_discount_amount !== undefined && formData.max_discount_amount < 0) {
      newErrors.max_discount_amount = 'Maximum discount amount cannot be negative';
    }

    if (formData.max_usage !== undefined && formData.max_usage < 1) {
      newErrors.max_usage = 'Max usage must be at least 1';
    }

    if (formData.max_usage_per_user !== undefined && formData.max_usage_per_user < 1) {
      newErrors.max_usage_per_user = 'Max usage per user must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setProcessing(true);
      await adminService.createCoupon(formData);
      toast.success('Coupon created successfully!');
      navigate('/admin/coupons');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create coupon');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/coupons')}
            className="bg-secondary-50 hover:bg-secondary-100 text-secondary-700 p-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Create New Coupon</h1>
            <p className="text-secondary-600 mt-1">Add a new coupon code to the platform</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-soft p-6 space-y-6">
        {/* Basic Information */}
        <div>
          <h2 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
            <Ticket className="text-primary-500" size={24} />
            <span>Basic Information</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Coupon Code <span className="text-danger-500">*</span>
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={formData.coupon_code}
                  onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value.toUpperCase() })}
                  className={`flex-1 px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 uppercase ${
                    errors.coupon_code ? 'border-danger-500' : 'border-secondary-200'
                  }`}
                  placeholder="Enter coupon code"
                  maxLength={20}
                />
                <button
                  type="button"
                  onClick={generateCouponCode}
                  className="px-4 py-3 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded-lg font-semibold transition-colors"
                >
                  Generate
                </button>
              </div>
              {errors.coupon_code && <p className="text-danger-500 text-sm mt-1">{errors.coupon_code}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={4}
                placeholder="Enter coupon description"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Associate with Offer (Optional)
              </label>
              <select
                value={formData.offer_id || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  offer_id: e.target.value ? parseInt(e.target.value) : null 
                })}
                className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">No Offer (Standalone Coupon)</option>
                {offers.map((offer) => (
                  <option key={offer.offer_id} value={offer.offer_id}>
                    {offer.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Discount Type <span className="text-danger-500">*</span>
              </label>
              <select
                value={formData.discount_type}
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as 'percentage' | 'fixed' })}
                className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Discount Value <span className="text-danger-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {formData.discount_type === 'percentage' ? (
                    <Percent className="text-secondary-400" size={20} />
                  ) : (
                    <DollarSign className="text-secondary-400" size={20} />
                  )}
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.discount_value ? 'border-danger-500' : 'border-secondary-200'
                  }`}
                  placeholder={formData.discount_type === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                />
              </div>
              {errors.discount_value && <p className="text-danger-500 text-sm mt-1">{errors.discount_value}</p>}
            </div>
          </div>
        </div>

        {/* Discount Limits */}
        <div>
          <h2 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
            <DollarSign className="text-success-500" size={24} />
            <span>Discount Limits</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Minimum Purchase Amount
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="text-secondary-400" size={20} />
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.min_purchase_amount || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    min_purchase_amount: e.target.value ? parseFloat(e.target.value) : undefined 
                  })}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.min_purchase_amount ? 'border-danger-500' : 'border-secondary-200'
                  }`}
                  placeholder="Optional"
                />
              </div>
              {errors.min_purchase_amount && <p className="text-danger-500 text-sm mt-1">{errors.min_purchase_amount}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Maximum Discount Amount
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="text-secondary-400" size={20} />
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.max_discount_amount || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    max_discount_amount: e.target.value ? parseFloat(e.target.value) : undefined 
                  })}
                  className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.max_discount_amount ? 'border-danger-500' : 'border-secondary-200'
                  }`}
                  placeholder="Optional"
                />
              </div>
              {errors.max_discount_amount && <p className="text-danger-500 text-sm mt-1">{errors.max_discount_amount}</p>}
            </div>
          </div>
        </div>

        {/* Validity & Usage */}
        <div>
          <h2 className="text-xl font-bold text-secondary-900 mb-4 flex items-center space-x-2">
            <Calendar className="text-info-500" size={24} />
            <span>Validity & Usage</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Valid From <span className="text-danger-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.valid_from ? 'border-danger-500' : 'border-secondary-200'
                }`}
              />
              {errors.valid_from && <p className="text-danger-500 text-sm mt-1">{errors.valid_from}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Valid Until <span className="text-danger-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={formData.valid_until}
                min={formData.valid_from || undefined}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.valid_until ? 'border-danger-500' : 'border-secondary-200'
                }`}
              />
              {errors.valid_until && <p className="text-danger-500 text-sm mt-1">{errors.valid_until}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Max Usage
              </label>
              <input
                type="number"
                min="1"
                value={formData.max_usage || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  max_usage: e.target.value ? parseInt(e.target.value) : undefined 
                })}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.max_usage ? 'border-danger-500' : 'border-secondary-200'
                }`}
                placeholder="Leave empty for unlimited"
              />
              {errors.max_usage && <p className="text-danger-500 text-sm mt-1">{errors.max_usage}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Max Usage Per User
              </label>
              <input
                type="number"
                min="1"
                value={formData.max_usage_per_user || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  max_usage_per_user: e.target.value ? parseInt(e.target.value) : undefined 
                })}
                className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.max_usage_per_user ? 'border-danger-500' : 'border-secondary-200'
                }`}
                placeholder="Leave empty for unlimited"
              />
              {errors.max_usage_per_user && <p className="text-danger-500 text-sm mt-1">{errors.max_usage_per_user}</p>}
            </div>
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-5 h-5 text-primary-500 border-secondary-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm font-semibold text-secondary-700">Active</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/admin/coupons')}
            className="px-6 py-3 border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 transition-colors flex items-center space-x-2"
          >
            <X size={18} />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            disabled={processing}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {processing ? (
              <Loader className="animate-spin" size={18} />
            ) : (
              <Save size={18} />
            )}
            <span>{processing ? 'Creating...' : 'Create Coupon'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCoupon;

